import { createSlice } from '@reduxjs/toolkit';
import FileFormatLink from '../../common/domain/FileFormatLink';
import { IngestFileType } from '../../common/domain/IngestFileType';
import IngestGroup from '../../common/domain/IngestGroup';
import BigQueryIngest from '../../common/domain/BigQueryIngest';
import { PickListState } from '../../pages/IngestPage/PickList';
import { Schema } from '../../pages/IngestPage/schema';
import fepLinearSchema from '../../pages/IngestPage/schema/FepLinear';
import * as reducers from './ingestReducers';
import * as mappingReducers from './mappingReducers';
import * as verificationReducers from './verificationReducers';
import { IndexToColumn } from '../../common/domain/mappings';

export interface ProgressState {
  type: string;
  message: string;
  value?: number;
}

/**
 * A map of column position in file to field name in target schema
 */

export interface MappingState {
  isCancellable: boolean;
  cancelling: boolean;
  group: IngestGroup;
  type: IngestFileType;
  fileName: string;
  rows: number;
  cols: number;
  hasHeader: boolean;
  previewRows: Array<Array<any>>;
  mapping: IndexToColumn;
  schema: Schema;
  ingestId: string;
}

export interface IngestState {
  isCancellable: boolean;
  loadingFileTypes: boolean;
  fileTypeSelector: PickListState<IngestFileType>;
  fileFormatSelector: PickListState<FileFormatLink>;
  groupSelector: PickListState<IngestGroup>;
  progress?: ProgressState;
  mapping?: MappingState;
  bqUpload?: BQUploadState;
  fileToUpload?: File;
  verification?: VerificationState;
  history?: any;
}

export interface VerificationState {
  validatedAt?: number;
  verificationSuccess?: boolean;
  errorsAccepted: boolean;
  numberOfErrors: number;
  rowsWithErrors: number;
  numberOfDuplicateRows: number;
  rowsUploaded: number;
  isCancellable: boolean;
  cancelling: boolean;
  ingestId: string;
  group: IngestGroup;
}

export interface BQUploadState {
  validatedAt?: number;
  verificationSuccess?: boolean;
  errorsAccepted: boolean;
  ingestId: string;
  group: IngestGroup;
  ingestedAt: number;
  bigQueryIngest?: BigQueryIngest;
}

interface IngestReducers {
  [reducer: string]: (state: IngestState, payload: any) => IngestState;
}

const initialPickListState = {
  isLoading: false,
  items: [],
};

const testMapping = {
  isCancellable: true,
  cancelling: false,
  submitting: false,
  group: { id: 'some group id', name: 'group name' },
  cols: 10,
  rows: 10,
  fileName: 'Some name',
  type: {
    id: 'FEP_LINEAR',
    internalId: 'FEP_LINEAR',
    internalType: 'FEP_LINEAR',
    name: 'FEP',
  },
  hasHeader: true,
  previewHeaders: ['a', 'b', 'c'],
  previewRows: [
    ['h1', 'h2', 'h3'],
    [1, 2, 3],
    [1, 2, 3],
  ],
  mapping: {},
  schema: fepLinearSchema,
  ingestId: '1212',
};

const initialState: IngestState = {
  isCancellable: false,
  loadingFileTypes: false,
  fileTypeSelector: initialPickListState,
  fileFormatSelector: initialPickListState,
  groupSelector: initialPickListState,
  //progress: {type:'upload',message:'Test',value:70}
  //mapping: testMapping
};

export default createSlice({
  name: 'ingest',
  initialState,
  reducers: { ...reducers, ...mappingReducers, ...verificationReducers },
});
